import { z } from 'zod';
import { cursorPageSchema } from '../api/http.js';
import { isoDateSchema, isoDateTimeSchema, reasonSchema, uuidSchema, } from '../common/primitives.js';
import { goalOptionSchema } from '../goals/goals.js';
import { memberRefSchema } from '../governance/topics.js';
import { localTimeSchema } from '../obligations/obligations.js';
import { taskSummarySchema } from '../tasks/tasks.js';
export const REPORTING_PERIOD_TYPES = ['WEEKLY', 'BIWEEKLY', 'MONTHLY'];
export const reportingPeriodTypeSchema = z.enum(REPORTING_PERIOD_TYPES);
export const WEEKLY_UPDATE_SECTIONS = [
    'DONE',
    'NEXT',
    'BLOCKERS',
    'NEED_DECISION',
    'RISKS',
    'METRICS',
];
export const weeklyUpdateSectionSchema = z.enum(WEEKLY_UPDATE_SECTIONS);
export const WEEKLY_UPDATE_SECTION_LABELS = {
    DONE: 'Done',
    NEXT: 'Next',
    BLOCKERS: 'Blockers',
    NEED_DECISION: 'Need decision',
    RISKS: 'Risks',
    METRICS: 'Metrics',
};
function weekdayOf(isoDate) {
    const day = new Date(`${isoDate}T00:00:00.000Z`).getUTCDay();
    return day === 0 ? 7 : day;
}
/**
 * Reporting cadence and required fields are open founder decision 12: nothing is assumed until the
 * workspace saves a schedule. Periods are cut on the calendar (ISO weeks / calendar months) in the
 * workspace time zone; the due instant is counted from the last day of the period.
 */
export const reportingScheduleSchema = z
    .object({
    enabled: z.boolean(),
    periodType: reportingPeriodTypeSchema,
    /** First day of the first period: a Monday (weekly/biweekly) or the 1st (monthly). */
    firstPeriodStartsOn: isoDateSchema,
    /** Days from the last day of the period to the due day (−6 = six days before the end). */
    dueOffsetDays: z.number().int().min(-6).max(14),
    dueTime: localTimeSchema,
    /** Late submissions are accepted for this long after the due time (0 = none). */
    lateSubmissionHours: z.number().int().min(0).max(336),
    /** Reminder to founders who have not submitted, this long before the due time (0 = off). */
    reminderHoursBeforeDue: z.number().int().min(0).max(168),
    /** Sections that must not be empty when submitting. */
    requiredSections: z
        .array(weeklyUpdateSectionSchema)
        .min(1)
        .max(WEEKLY_UPDATE_SECTIONS.length)
        .refine((sections) => new Set(sections).size === sections.length, 'Duplicate section'),
})
    .refine((value) => value.periodType === 'MONTHLY'
    ? value.firstPeriodStartsOn.endsWith('-01')
    : weekdayOf(value.firstPeriodStartsOn) === 1, {
    message: 'Weekly periods start on a Monday; monthly periods on the 1st',
    path: ['firstPeriodStartsOn'],
});
export const reportingSettingsSchema = z.object({
    /** False until the owner saves a schedule (open decision 12). */
    configured: z.boolean(),
    schedule: reportingScheduleSchema.nullable(),
    timezone: z.string(),
    /** 0 when never saved. */
    version: z.number().int().min(0),
});
export const updateReportingSettingsRequestSchema = z.object({
    schedule: reportingScheduleSchema,
    version: z.number().int().min(0),
    reason: reasonSchema,
});
export const REPORTING_PERIOD_STATUSES = ['UPCOMING', 'OPEN', 'CLOSED'];
export const reportingPeriodStatusSchema = z.enum(REPORTING_PERIOD_STATUSES);
export const reportingPeriodSchema = z.object({
    id: uuidSchema,
    periodType: reportingPeriodTypeSchema,
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    dueAt: isoDateTimeSchema,
    /** After this instant no new submissions or amendments are accepted. */
    closesAt: isoDateTimeSchema,
    status: reportingPeriodStatusSchema,
});
/**
 * Per-founder state for a period (spec §6.6: submitted / late / missing). Derived from facts; never
 * a score.
 */
export const REPORT_STATES = [
    'NOT_STARTED',
    'DRAFT',
    'SUBMITTED',
    'LATE',
    'OVERDUE',
    'MISSING',
    'EXCUSED',
    'NOT_EXPECTED',
];
export const reportStateSchema = z.enum(REPORT_STATES);
/** Shared by the API, the scheduler and the web so the state is computed the same way everywhere. */
export function reportStateOf(facts, now) {
    if (facts.status === 'SUBMITTED') {
        return facts.isLate === true ? 'LATE' : 'SUBMITTED';
    }
    if (facts.excused)
        return 'EXCUSED';
    if (!facts.expected)
        return facts.status === 'DRAFT' ? 'DRAFT' : 'NOT_EXPECTED';
    const time = now.getTime();
    if (time >= Date.parse(facts.closesAt))
        return 'MISSING';
    if (time >= Date.parse(facts.dueAt))
        return 'OVERDUE';
    return facts.status === 'DRAFT' ? 'DRAFT' : 'NOT_STARTED';
}
const sectionText = z.string().trim().max(10000).nullable();
export const updateSectionsSchema = z.object({
    done: sectionText,
    next: sectionText,
    blockers: sectionText,
    needDecision: sectionText,
    risks: sectionText,
    metrics: sectionText,
});
export const SECTION_FIELDS = {
    DONE: 'done',
    NEXT: 'next',
    BLOCKERS: 'blockers',
    NEED_DECISION: 'needDecision',
    RISKS: 'risks',
    METRICS: 'metrics',
};
export const UPDATE_LINK_TYPES = [
    'TASK',
    'TOPIC',
    'DECISION',
    'OBLIGATION',
    'BLOCKER',
    'GOAL',
];
export const updateLinkTypeSchema = z.enum(UPDATE_LINK_TYPES);
export const updateLinkInputSchema = z.object({
    section: weeklyUpdateSectionSchema,
    entityType: updateLinkTypeSchema,
    entityId: uuidSchema,
    note: z.string().trim().max(500).nullable().optional(),
});
export const saveWeeklyUpdateRequestSchema = updateSectionsSchema
    .partial()
    .extend({
    /** 0 when no draft exists yet. */
    version: z.number().int().min(0),
    links: z.array(updateLinkInputSchema).max(50).default([]),
})
    .refine((value) => new Set(value.links.map((link) => `${link.section}:${link.entityType}:${link.entityId}`))
    .size === value.links.length, { message: 'The same item is linked twice in one section', path: ['links'] });
/** A reported link; items the reader cannot see keep their place but lose their label. */
export const updateLinkSchema = z.object({
    section: weeklyUpdateSectionSchema,
    entityType: updateLinkTypeSchema,
    entityId: uuidSchema,
    note: z.string().nullable(),
    visible: z.boolean(),
    label: z.string().nullable(),
    /** In-app path relative to the workspace, e.g. `/tasks/<id>`. */
    link: z.string().nullable(),
    /** Current status of the linked item (task status, decision status…), for commitment checks. */
    currentStatus: z.string().nullable(),
});
export const updateRevisionRefSchema = z.object({
    revision: z.number().int(),
    createdAt: isoDateTimeSchema,
    createdBy: z.string(),
});
export const weeklyUpdateSchema = updateSectionsSchema.extend({
    id: uuidSchema,
    periodId: uuidSchema,
    author: memberRefSchema,
    status: z.enum(['DRAFT', 'SUBMITTED']),
    state: reportStateSchema,
    links: z.array(updateLinkSchema),
    submittedAt: isoDateTimeSchema.nullable(),
    isLate: z.boolean().nullable(),
    currentRevision: z.number().int(),
    revisions: z.array(updateRevisionRefSchema),
    canEdit: z.boolean(),
    updatedAt: isoDateTimeSchema,
    version: z.number().int().min(1),
});
export const weeklyUpdateRevisionSchema = updateSectionsSchema.extend({
    revision: z.number().int(),
    createdAt: isoDateTimeSchema,
    createdBy: z.string(),
    links: z.array(updateLinkSchema),
});
/** Last period's "Next" commitments, shown while writing the new update (spec §19.3). */
export const previousCommitmentsSchema = z.object({
    period: reportingPeriodSchema,
    updateId: uuidSchema,
    next: z.string().nullable(),
    links: z.array(updateLinkSchema),
});
export const reporterRowSchema = z.object({
    member: memberRefSchema,
    state: reportStateSchema,
    updateId: uuidSchema.nullable(),
    submittedAt: isoDateTimeSchema.nullable(),
    excusedReason: z.string().nullable(),
});
export const periodPermissionsSchema = z.object({
    canSubmit: z.boolean(),
    canExcuse: z.boolean(),
});
export const myUpdateViewSchema = z.object({
    period: reportingPeriodSchema,
    requiredSections: z.array(weeklyUpdateSectionSchema),
    timezone: z.string(),
    state: reportStateSchema,
    update: weeklyUpdateSchema.nullable(),
    previousCommitments: previousCommitmentsSchema.nullable(),
    /** Open tasks owned by the author, offered as links. */
    suggestedTasks: z.array(taskSummarySchema),
    /** Active goals the update can report progress against. */
    suggestedGoals: z.array(goalOptionSchema),
    permissions: periodPermissionsSchema,
});
export const currentUpdateSchema = z.object({
    configured: z.boolean(),
    /** `null` when updates are not configured/enabled or no period is open yet. */
    view: myUpdateViewSchema.nullable(),
});
export const periodSummarySchema = reportingPeriodSchema.extend({
    myState: reportStateSchema,
    expected: z.number().int(),
    submitted: z.number().int(),
    late: z.number().int(),
    missing: z.number().int(),
    excused: z.number().int(),
});
export const periodPageSchema = cursorPageSchema(periodSummarySchema);
export const periodListQuerySchema = z.object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(52).default(12),
});
export const periodDetailSchema = z.object({
    period: reportingPeriodSchema,
    requiredSections: z.array(weeklyUpdateSectionSchema),
    reporters: z.array(reporterRowSchema),
    permissions: periodPermissionsSchema,
});
export const weeklyUpdateDetailSchema = z.object({
    period: reportingPeriodSchema,
    update: weeklyUpdateSchema,
});
export const excuseReporterRequestSchema = z.object({ reason: reasonSchema });
/**
 * Facts-only digest of one period (spec §6.6, §19.3): commitments from the previous period next to
 * what was reported. Reporters are listed alphabetically — never ranked.
 */
export const digestEntrySchema = z.object({
    member: memberRefSchema,
    state: reportStateSchema,
    updateId: uuidSchema.nullable(),
    done: z.string().nullable(),
    next: z.string().nullable(),
    blockers: z.string().nullable(),
    needDecision: z.string().nullable(),
    /** What the member said they would do, in the previous period's update. */
    previousNext: z.string().nullable(),
    /** Tasks linked as "Next" last period, with their status now. */
    previousCommitments: z.array(updateLinkSchema),
    links: z.array(updateLinkSchema),
    tasksCompleted: z.array(taskSummarySchema),
});
export const periodDigestSchema = z.object({
    period: reportingPeriodSchema,
    previousPeriod: reportingPeriodSchema.nullable(),
    entries: z.array(digestEntrySchema),
    totals: z.object({
        expected: z.number().int(),
        submitted: z.number().int(),
        late: z.number().int(),
        missing: z.number().int(),
        excused: z.number().int(),
        tasksCompleted: z.number().int(),
        needDecisionItems: z.number().int(),
    }),
});
//# sourceMappingURL=updates.js.map